import React, { useState, useEffect } from "react";
import { Card, Space, Spin, Alert, Typography, Statistic, Row, Col, Progress, Divider } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, DollarOutlined, GlobalOutlined, SafetyOutlined } from "@ant-design/icons";
import PageHeader from "../../components/common/PageHeader";
import consumosService from "../../services/consumos";
import type { ConsumosResponse } from "../../services/consumos";

const { Text } = Typography;

type ProgressStatus = "success" | "active" | "exception";

function getProgressStyle(percent: number): { strokeColor: string; status: ProgressStatus } {
  if (percent >= 90) {
    return { strokeColor: "#ff4d4f", status: "exception" };
  }
  if (percent >= 70) {
    return { strokeColor: "#fa8c16", status: "active" };
  }
  return { strokeColor: "#52c41a", status: "success" };
}

/** Converte megabytes para gigabytes formatados para exibição. */
function formatMbToGb(mb: number): string {
  const gb = mb / 1024;
  return gb.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Página de Captchas / Proxies.
 *
 * Exibe o consumo/saldo dos serviços de captcha e proxies.
 */
const ConsumosContent: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ConsumosResponse["data"] | null>(null);

  const fetchConsumos = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await consumosService.getConsumos();
      if (response.success) {
        setData(response.data);
      } else {
        setError("Erro ao buscar consumos");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao buscar consumos");
      console.error("Erro ao buscar consumos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsumos();
  }, []);

  if (loading) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Captchas / Proxies" />
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "40px" }} />
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Captchas / Proxies" />
          <Alert message="Erro ao carregar consumos" description={error} type="error" showIcon />
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PageHeader title="Captchas / Proxies" />

        {/* Seção de Quebradores */}
        <Divider orientation="left">
          <Text strong style={{ fontSize: "18px" }}>
            <SafetyOutlined /> Quebradores
          </Text>
        </Divider>

        <Row gutter={[16, 16]}>
          {/* Nopecha */}
          <Col xs={24} sm={24} md={8}>
            <Card style={{ height: "100%" }}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Nopecha
                  </Text>
                  {data?.nopecha.success ? (
                    <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
                  )}
                </div>
                {data?.nopecha.success && data.nopecha.data ? (
                  <div>
                    {(() => {
                      const consumed = data.nopecha.data.quota - data.nopecha.data.credit;
                      const percent = Math.round((consumed / data.nopecha.data.quota) * 100);
                      const { strokeColor, status } = getProgressStyle(percent);

                      return (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                            <Text type="secondary">Créditos:</Text>
                            <Text strong>
                              {consumed.toLocaleString()} / {data.nopecha.data.quota.toLocaleString()}
                            </Text>
                          </div>
                          <Progress percent={percent} status={status} strokeColor={strokeColor} showInfo={false} />
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <Alert
                    message="Erro"
                    description={data?.nopecha.error || "Erro desconhecido"}
                    type="error"
                    showIcon
                  />
                )}
              </Space>
            </Card>
          </Col>

          {/* Capsolver */}
          <Col xs={24} sm={12} md={8}>
            <Card style={{ height: "100%" }}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Capsolver
                  </Text>
                  {data?.capsolver.success ? (
                    <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
                  )}
                </div>
                {data?.capsolver.success ? (
                  <Statistic
                    title="Saldo"
                    value={data.capsolver.balance ?? undefined}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: "#3f8600" }}
                  />
                ) : (
                  <Alert
                    message="Erro"
                    description={data?.capsolver.error || "Erro desconhecido"}
                    type="error"
                    showIcon
                  />
                )}
              </Space>
            </Card>
          </Col>

          {/* CapMonster */}
          <Col xs={24} sm={12} md={8}>
            <Card style={{ height: "100%" }}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    CapMonster
                  </Text>
                  {data?.capmonster.success ? (
                    <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
                  )}
                </div>
                {data?.capmonster.success ? (
                  <Statistic
                    title="Saldo"
                    value={data.capmonster.balance ?? undefined}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: "#3f8600" }}
                  />
                ) : (
                  <Alert
                    message="Erro"
                    description={data?.capmonster.error || "Erro desconhecido"}
                    type="error"
                    showIcon
                  />
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Seção de Proxies */}
        <Divider orientation="left" style={{ marginTop: "24px" }}>
          <Text strong style={{ fontSize: "18px" }}>
            <GlobalOutlined /> Proxies
          </Text>
        </Divider>

        <Row gutter={[16, 16]}>
          {/* Webshare */}
          <Col xs={24} sm={24} md={12}>
            <Card>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Webshare
                  </Text>
                  {data?.webshare.success ? (
                    <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
                  )}
                </div>
                {data?.webshare.success && data.webshare.data ? (
                  data.webshare.data.aggregate.bandwidth_projected > 0 ? (
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                        <Text type="secondary">Bandwidth:</Text>
                        <Text strong>
                          {formatMbToGb(data.webshare.data.aggregate.bandwidth_total)} /{" "}
                          {formatMbToGb(data.webshare.data.aggregate.bandwidth_projected)} GB
                        </Text>
                      </div>
                      {(() => {
                        const percent = Math.round(
                          (data.webshare.data.aggregate.bandwidth_total /
                            data.webshare.data.aggregate.bandwidth_projected) *
                            100
                        );
                        const { strokeColor, status } = getProgressStyle(percent);

                        return (
                          <Progress
                            percent={percent}
                            status={status}
                            strokeColor={strokeColor}
                            showInfo={false}
                          />
                        );
                      })()}
                    </div>
                  ) : (
                    <Text type="secondary">Sem dados de bandwidth disponíveis</Text>
                  )
                ) : (
                  <Alert
                    message="Erro"
                    description={data?.webshare.error || "Erro desconhecido"}
                    type="error"
                    showIcon
                  />
                )}
              </Space>
            </Card>
          </Col>

          {/* DataImpulse */}
          <Col xs={24} sm={24} md={12}>
            <Card>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    DataImpulse
                  </Text>
                  {data?.dataimpulse.success ? (
                    <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
                  )}
                </div>
                {data?.dataimpulse.success && data.dataimpulse.data ? (
                  data.dataimpulse.data.bandwidth_total > 0 ? (
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                        <Text type="secondary">Bandwidth Disponível:</Text>
                        <Text strong>
                          {formatMbToGb(data.dataimpulse.data.bandwidth_available)} GB
                        </Text>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                        <Text type="secondary">Bandwidth:</Text>
                        <Text strong>
                          {formatMbToGb(data.dataimpulse.data.bandwidth_used)} /{" "}
                          {formatMbToGb(data.dataimpulse.data.bandwidth_total)} GB
                        </Text>
                      </div>
                      {(() => {
                        const percent = Math.round(
                          (data.dataimpulse.data.bandwidth_used / data.dataimpulse.data.bandwidth_total) * 100
                        );
                        const { strokeColor, status } = getProgressStyle(percent);

                        return (
                          <Progress
                            percent={percent}
                            status={status}
                            strokeColor={strokeColor}
                            showInfo={false}
                          />
                        );
                      })()}
                    </div>
                  ) : (
                    <Text type="secondary">Sem dados de bandwidth disponíveis</Text>
                  )
                ) : (
                  <Alert
                    message="Erro"
                    description={data?.dataimpulse.error || "Erro desconhecido"}
                    type="error"
                    showIcon
                  />
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

const Consumos: React.FC = () => {
  return (
    <ConsumosContent />
  );
};

export default Consumos;
